/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
/** @jsx jsx */
import { jsx } from "@emotion/core"
import { find } from "lodash"
import { Fragment, useEffect, useMemo, useRef, useState } from "react"
import { useParams, useHistory, useLocation } from "react-router-dom"
import { useDispatch, useSelector } from "react-redux"
import { actionCreator } from "store/store"
import { CATALOGUES, MENUS } from "constants/routes"
import { useTranslationsMap } from "common/utils/i18n"
import {
  WithMenuHeader,
  WithMenuHeaderInjectedProps,
} from "components/hoc/WithMenuHeader"
import { storageHelper } from "utils/storage"
import { getResponseErrorEventLabels, TroyWebTracker } from "utils/tracking"
import {
  EventAction,
  EventActionEng,
  EventCategoryEng,
} from "constants/tracking"
import {
  isMartDepartmentStructure,
  getIsSellingTimeStructure,
  isMartDepartmentV2Structure,
  getIsSellingTimeStructureV2,
  getBusinessTypeGrabXFacetVal,
} from "utils/menu_store_helper"
import { getItemList, getItemWithoutPhotoCount } from "utils/menu_helper"
import { AppState } from "store/store.type"
import { ResponseWithError } from "types/api"
import { MenuEditorContent } from "components/MenuEditorContent"
import { MenuDrawerContainer } from "containers/MenuDrawerContainer"
import useInventoryContext from "hooks/useInventoryContext"
import useSingleStoreUser from "hooks/useSingleStoreUser"
import {
  BusinessType,
  Menu,
  MenuGroup,
  MerchantType,
  Section,
} from "types/models/menu"
import { getIsMenuGroupMartDepartmentStructure } from "utils/menu_group"
import { MenuEditorTabKey, menuTabEventCategories } from "constants/menu"
import {
  getGrabXValue,
  FacetTypes,
  GrabXDataTypes,
  MerchantPortalFeatureFlag,
  withObserverModeFacet,
} from "grabx"
import { getUserCountry } from "troy-common/utils"
import { getUserCityID } from "utils/authentication"
import { actions as menuDuckActions } from "store/duck/menu"
import { getActionFail, getActionSuccess } from "store/duck/utils/actionUtils"
import useRtkQueryMigrationEndpointsFlag from "hooks/useRtkQueryMigrationEndpointsFlag"
import { GetErrorNotificationContent } from "queries/apiClient"
import {
  useLazyFetchMerchantDetailQuery,
  useLazyFetchDepartmentsQuery,
  useLazyFetchMenuGroupDetailQuery,
  useLazyFetchCatalogueQuery,
  useLazyFetchMenuQuery,
} from "queries/menuApi"
import {
  fetchDepartmentsWithMigration,
  fetchMenuGroupDetailWithMigration,
  fetchCatalogueWithMigration,
  fetchMenuWithMigration,
} from "utils/rtk_migration_helper"
import { StockManagementAutoScrollProvider } from "./StockManagementAutoScrollContext"
import { useShownTabs } from "./hooks/useShownTab"

interface MenuEditorProps {
  routePrefix?: string
  eventLabels?: { [key: string]: string }
}

export const MenuEditor = ({
  routePrefix: propRoutePrefix,
  eventLabels,
}: MenuEditorProps) => {
  const { menuParentID, tabKey } = useParams<{
    menuParentID: string
    tabKey: string
  }>()
  const location = useLocation()

  const dispatch = useDispatch()
  const [hasInitialised, setHasInitialised] = useState(false)

  const {
    flags: {
      fetchMerchantDetail: isFetchMerchantDetailRtkEnabled,
      fetchDepartments: isRtkFetchDepartmentsEnabled,
      fetchMenuGroupDetail: isFetchMenuGroupDetailRtkEnabled,
      fetchCatalogue: isFetchCatalogueRtkEnabled,
      fetchMenu: isFetchMenuRtkEnabled,
    },
    isLoading: isLoadingRtkQueryMigrationEndpointsFlag,
  } = useRtkQueryMigrationEndpointsFlag()
  const [triggerFetchMerchantDetailRtk] = useLazyFetchMerchantDetailQuery()
  const [triggerFetchDepartmentsRtk] = useLazyFetchDepartmentsQuery()
  const [triggerFetchMenuGroupDetailRtk] = useLazyFetchMenuGroupDetailQuery()
  const [triggerFetchCatalogueRtk] = useLazyFetchCatalogueQuery()
  const [triggerFetchMenuRtk] = useLazyFetchMenuQuery()

  const history = useHistory()

  const {
    isMartStore,
    isMenuGroup,
    merchantGroupID,
    isSellingTimeStructure,
    items,
  } = useSelector((state: AppState) => state.menu)

  const { isInventory } = useInventoryContext()

  // Generation counter for in-flight menu loads. Navigating between menu groups
  // starts a new fetchMenu (which clears menu state first), so everything the
  // previous load would still do - dispatching its flags, and fetching that group's
  // detail/menu - has to stop once it has been superseded.
  const menuRunRef = useRef(0)

  const menuGroupID = isMenuGroup && !isInventory ? menuParentID : undefined
  const merchantID = isMenuGroup && !isInventory ? undefined : menuParentID

  const routePrefix = useMemo(
    () => propRoutePrefix ?? (isMartStore ? CATALOGUES : MENUS),
    [propRoutePrefix, isMartStore],
  )

  const countryCode = storageHelper.getUserCountry() || ""

  const shownTabs = useShownTabs()

  const activeTabKey = shownTabs.includes(tabKey as MenuEditorTabKey)
    ? tabKey
    : shownTabs[0]

  const fetchMerchantDetail = async () => {
    if (!merchantID) return undefined
    if (isFetchMerchantDetailRtkEnabled) {
      const pendingAction = {
        type: menuDuckActions.FETCH_MERCHANT_DETAIL,
        payload: { merchantID },
      }
      dispatch(pendingAction)

      const result = await triggerFetchMerchantDetailRtk({
        merchantID,
        countryCode,
      })

      if (result.error) {
        const rtkError = result.error as {
          message?: string
          status?: number
          data?: unknown
          headers?: Record<string, string>
        }
        const failAction = {
          type: getActionFail(menuDuckActions.FETCH_MERCHANT_DETAIL),
          payload: { data: {} },
          // Keep request metadata absent so redux-axios-middleware does not
          // attempt to execute this synthetic RTK compatibility action.
          meta: { previousAction: pendingAction },
          error: {
            message: rtkError.message,
            response: {
              status: rtkError.status,
              data: rtkError.data,
              headers: rtkError.headers,
            },
          },
        }
        dispatch(failAction)
        TroyWebTracker.sendEvent({
          category: EventCategoryEng.rtkMigration,
          action: EventActionEng.fetchMerchantDetail,
          labels: {
            MERCHANT_ID: merchantID,
            IS_RTK_ENABLED: isFetchMerchantDetailRtkEnabled,
            FROM_RTK_QUERY: true,
            FROM_OLD_REDUX: false,
            ACTION_NAME: "fetchMerchantDetail",
          },
        })
        return failAction
      }

      // Sync Redux state so downstream consumers (isMartStore, outletV2, etc.) stay up-to-date
      const successAction = {
        type: getActionSuccess(menuDuckActions.FETCH_MERCHANT_DETAIL),
        payload: { data: result.data },
        meta: { previousAction: pendingAction },
      }
      dispatch(successAction)
      TroyWebTracker.sendEvent({
        category: EventCategoryEng.rtkMigration,
        action: EventActionEng.fetchMerchantDetail,
        labels: {
          MERCHANT_ID: merchantID,
          IS_RTK_ENABLED: isFetchMerchantDetailRtkEnabled,
          FROM_RTK_QUERY: true,
          FROM_OLD_REDUX: false,
          ACTION_NAME: "fetchMerchantDetail",
        },
      })
      return successAction
    }

    TroyWebTracker.sendEvent({
      category: EventCategoryEng.rtkMigration,
      action: EventActionEng.fetchMerchantDetail,
      labels: {
        MERCHANT_ID: merchantID,
        IS_RTK_ENABLED: isFetchMerchantDetailRtkEnabled,
        FROM_RTK_QUERY: false,
        FROM_OLD_REDUX: true,
        ACTION_NAME: "fetchMerchantDetail",
      },
    })
    return dispatch(
      actionCreator.menu.fetchMerchantDetail({
        merchantID,
        countryCode,
      }),
    )
  }

  const fetchMenuGroupDetail = async (isSuperseded?: () => boolean) => {
    if (!menuGroupID) return undefined
    return fetchMenuGroupDetailWithMigration({
      dispatch,
      fetchMenuGroupDetailRtk: triggerFetchMenuGroupDetailRtk,
      isRtkFetchMenuGroupDetailEnabled: isFetchMenuGroupDetailRtkEnabled,
      request: {
        menuGroupID,
        merchantGroupID,
      },
      isSuperseded,
    })
  }

  const textMap = useTranslationsMap({
    errorExceedMaxCategoryLimit:
      "catalogue.errors.category.exceedMaxCategoryLimit",
  })

  const getIsMenuParentSellingTimeStructure = (merchantType: MerchantType) => {
    const store = merchantType
    if ("merchantID" in store) {
      return getIsSellingTimeStructure(store)
    }
    return getIsSellingTimeStructureV2(store)
  }

  const getIsMartDepartmentStructure = (merchantType: MerchantType) => {
    const store = merchantType
    if ("merchantID" in store) {
      return isMartDepartmentStructure(store)
    }
    return isMartDepartmentV2Structure(store)
  }

  const fetchCatalogueErrorCallback = (response: ResponseWithError) => {
    const { message = "", reason = "" } = response ?? {}

    if (message === "too many item classes" && reason === "rate_exceeded") {
      return textMap.errorExceedMaxCategoryLimit
    }

    return true
  }

  const getFetchCatalogueErrorNotificationContent: GetErrorNotificationContent = ({
    data,
    defaultMessage,
  }) => {
    const message = fetchCatalogueErrorCallback(data as ResponseWithError)
    return {
      message: typeof message === "string" ? message : defaultMessage,
    }
  }

  const fetchCatalogue = async (
    isMenuParentSellingTimeStructure: boolean,
    isSuperseded?: () => boolean,
  ) => {
    const [resp] = await Promise.all([
      fetchCatalogueWithMigration({
        dispatch,
        dispatchLegacyFetch: () =>
          dispatch(
            actionCreator.menu.fetchCatalogue({
              merchantID,
              menuGroupID,
              merchantGroupID,
              showGlobalLoading: true,
              errorCallback: fetchCatalogueErrorCallback,
              isSellingTimeStructure: isMenuParentSellingTimeStructure,
            }),
          ),
        fetchCatalogueRtk: triggerFetchCatalogueRtk,
        isRtkFetchCatalogueEnabled: isFetchCatalogueRtkEnabled,
        isRefreshing: true,
        request: {
          merchantID,
          menuGroupID,
          merchantGroupID,
          isSellingTimeStructure: isMenuParentSellingTimeStructure,
          showGlobalLoading: true,
          getErrorNotificationContent: getFetchCatalogueErrorNotificationContent,
        },
      }),
      fetchDepartmentsWithMigration({
        dispatch,
        trigger: triggerFetchDepartmentsRtk,
        isRtkEnabled: isRtkFetchDepartmentsEnabled,
        merchantID: merchantID || "",
        request: {
          merchantID,
          menuGroupID,
          merchantGroupID,
        },
        isSuperseded,
      }),
    ])
    return resp
  }

  const fetchMenuFeatureFlag = async (runID: number) => {
    // menuGroupID is only set for a real menu-group page (isMenuGroup && !isInventory),
    // so it is the page-correct predicate - Redux isMenuGroup alone is true on the
    // inventory pages of a menu-group-enabled merchant group too.
    const isMenuGroupPage = !!menuGroupID
    const facets = {
      [FacetTypes.Merchant]: merchantID,
      [FacetTypes.MexMerchant]: merchantGroupID,
      [FacetTypes.City]: getUserCityID(),
      [FacetTypes.Country]: getUserCountry(),
    }

    // These lookups are independent, and fetchMenu awaits this before any
    // menu/merchant request starts - keep them parallel so the editor's load path
    // (and the LOAD_TIME window) does not grow a round trip per flag.
    const [
      enableAlcoholSetting,
      enableBcrsMenuGroupV2,
      enableTobaccoSelling,
      enablePartialMenu,
    ] = await Promise.all([
      getGrabXValue(
        isMenuGroupPage
          ? MerchantPortalFeatureFlag.enableAlcoholSettingMenuGroupV2
          : MerchantPortalFeatureFlag.enableAlcoholSetting,
        GrabXDataTypes.Boolean,
        false,
        facets,
      ),
      // BCRS on the Menu Group V2 editor is gated by its own GrabX flag, since the
      // menu-group page has no merchant.featureFlags/outletV2 to read enableBCRS
      // from (store and inventory modes still read outletV2 in useIsBcrsEnabled).
      isMenuGroupPage
        ? getGrabXValue(
            MerchantPortalFeatureFlag.enableBCRSForMenuGroupV2,
            GrabXDataTypes.Boolean,
            false,
            facets,
          )
        : Promise.resolve(false),
      getGrabXValue(
        isMenuGroupPage
          ? MerchantPortalFeatureFlag.TobaccoSaleEnabledForMenuGroupV2
          : MerchantPortalFeatureFlag.TobaccoSaleEnabled,
        GrabXDataTypes.Boolean,
        false,
        facets,
      ),
      getGrabXValue(
        MerchantPortalFeatureFlag.portalEnablePartialMenu,
        GrabXDataTypes.Boolean,
        false,
        withObserverModeFacet(facets),
      ),
    ])

    // A newer lookup started while these were in flight - its results win, and
    // dispatching ours now would resurrect the previous menu group's flags after
    // clearMenu() already reset them.
    if (runID !== menuRunRef.current) {
      return { enablePartialMenu: false, superseded: true }
    }

    dispatch(actionCreator.menu.setAlcoholSetting(enableAlcoholSetting))
    dispatch(
      actionCreator.menu.setBcrsMenuGroupV2(
        enableBcrsMenuGroupV2,
        isMenuGroupPage,
      ),
    )
    dispatch(actionCreator.menu.setTobaccoSetting(enableTobaccoSelling))

    return { enablePartialMenu, superseded: false }
  }

  const fetchBusinessTypeFeatureFlag = async (businessType: string) => {
    const facets = {
      [FacetTypes.Merchant]: merchantID,
      [FacetTypes.City]: getUserCityID(),
      [FacetTypes.Country]: getUserCountry(),
      [FacetTypes.BusinessType]: businessType,
    }

    const enableMenuTranslationPortalCall = await getGrabXValue(
      MerchantPortalFeatureFlag.enableMenuTranslationPortal,
      GrabXDataTypes.Boolean,
      false,
      facets,
    )
    const enableGrabMexProImageEditor = await getGrabXValue(
      MerchantPortalFeatureFlag.portalEnableGrabMexProImageEditor,
      GrabXDataTypes.Boolean,
      false,
      withObserverModeFacet(facets),
    )

    dispatch(
      actionCreator.menu.setEnableMenuTranslationPortal(
        enableMenuTranslationPortalCall,
      ),
    )
    dispatch(
      actionCreator.menu.setEnableGrabMexProImageEditor(
        enableGrabMexProImageEditor,
      ),
    )
  }

  const fetchBusinessTypeMenuGroupFeatureFlag = async (
    businessType: string,
    runID?: number,
  ) => {
    const facets = {
      [FacetTypes.MexMerchant]: merchantGroupID,
      [FacetTypes.Country]: getUserCountry(),
      [FacetTypes.BusinessType]: businessType,
    }

    const enableMenuGroupMenuTranslationPortalCall = await getGrabXValue(
      MerchantPortalFeatureFlag.enableMenuGroupMenuTranslationPortal,
      GrabXDataTypes.Boolean,
      false,
      facets,
    )

    // Same supersession rule as the menu feature flags: this lookup is awaited
    // mid-load, so a navigation during it must not let the previous menu group's
    // business-type config land on the newly opened page.
    if (runID !== undefined && runID !== menuRunRef.current) {
      return
    }

    dispatch(
      actionCreator.menu.setEnableMenuTranslationPortal(
        enableMenuGroupMenuTranslationPortalCall,
      ),
    )
    // EnhanceImage API is not available for menu group yet
    dispatch(actionCreator.menu.setEnableGrabMexProImageEditor(false))
  }

  const fetchMenuNonDepartment = async (
    isMenuParentSellingTimeStructure: boolean,
  ) => {
    const lastSegment = location.pathname.split("/").pop()

    if (isLoadingRtkQueryMigrationEndpointsFlag) {
      return undefined
    }

    const resp = await fetchMenuWithMigration({
      dispatch,
      merchantID,
      merchantGroupID,
      menuGroupID,
      isSellingTimeStructure: isMenuParentSellingTimeStructure,
      isRtkFetchMenuEnabled: isFetchMenuRtkEnabled,
      fetchMenuRtk: triggerFetchMenuRtk,
      showGlobalLoading: true,
    })

    const menu = resp?.payload?.data as Menu | undefined
    if (menu) {
      const itemList = getItemList(menu)
      const selectedSectionID = itemList.find(
        item => item.itemID === lastSegment,
      )?.sectionID

      if (!isMenuParentSellingTimeStructure && menu.sections) {
        if (selectedSectionID) {
          const section: Section =
            find(
              menu.sections,
              ({ sectionID }) => sectionID === selectedSectionID,
            ) ?? menu.sections[0]
          await dispatch(actionCreator.menu.selectSection(section))
        }
      }
    }
    return resp
  }

  const fetchMenu = async () => {
    const runID = menuRunRef.current + 1
    menuRunRef.current = runID
    // Checked at every await boundary: a superseded load must not start a request
    // for the menu group the user has already navigated away from, because those
    // responses dispatch into the shared menu state on arrival.
    const isSuperseded = () => runID !== menuRunRef.current

    const t0 = performance.now()
    dispatch(actionCreator.menu.clearMenu())
    const { enablePartialMenu, superseded } = await fetchMenuFeatureFlag(runID)
    if (superseded || isSuperseded()) return

    let resp: any
    if (menuGroupID) resp = await fetchMenuGroupDetail(isSuperseded)
    else resp = await fetchMerchantDetail()
    if (isSuperseded()) return

    if (!resp?.type?.endsWith("_FAIL")) {
      const menuGroup: MenuGroup | undefined = resp?.payload?.data?.menuGroup
      const merchant: MerchantType | undefined = resp?.payload?.data?.merchant

      if (menuGroup) {
        await fetchBusinessTypeMenuGroupFeatureFlag(
          getBusinessTypeGrabXFacetVal(
            menuGroup.businessType || BusinessType.Food,
          ), // endpoint return empty when the businessType is 0
          runID,
        )
        if (isSuperseded()) return
        const isMartDept = getIsMenuGroupMartDepartmentStructure(menuGroup)
        resp = isMartDept
          ? await fetchCatalogue(isSellingTimeStructure, isSuperseded)
          : await fetchMenuNonDepartment(isSellingTimeStructure)
      } else if (merchant) {
        await fetchBusinessTypeFeatureFlag(
          getBusinessTypeGrabXFacetVal(
            merchant.businessType || BusinessType.Food,
          ), // endpoint return empty when the businessType is 0
        )
        const merchantIsSellingTimeStructure = getIsMenuParentSellingTimeStructure(
          merchant,
        )
        if (isSuperseded()) return
        const isMartDept = getIsMartDepartmentStructure(merchant)
        // Skip fetching full menu/catalogue only when the partial menu API path is
        // actually going to serve this store - it does not cover menu-group-owned
        // merchants (see useEnablePartialMenu), so this must mirror that same gate or
        // menu-group Mart stores fall into the gap between both fetch paths.
        if (isMartDept && enablePartialMenu && !isMenuGroup) {
          dispatch(actionCreator.menu.setEnablePartialMenu(true))
          // Partial menu API handles fetching, so we skip fetchCatalogue
          resp = { type: "SKIP_FETCH" }
        } else {
          resp = isMartDept
            ? await fetchCatalogue(merchantIsSellingTimeStructure, isSuperseded)
            : await fetchMenuNonDepartment(merchantIsSellingTimeStructure)
        }
      }
    } else {
      TroyWebTracker.sendEvent({
        category: EventCategoryEng.errorMerchantV2,
        action: EventActionEng.errorFetchMerchantV2,
        labels: {
          merchantID: resp?.meta?.previousAction?.payload?.merchantID,
          errorMessage: resp?.error,
          username: storageHelper.getEmail(),
        },
      })
    }

    const t1 = performance.now()

    const category = menuTabEventCategories[activeTabKey]

    let labels = {
      ...(merchantID
        ? { STORE_ID: merchantID }
        : { MERCHANT_GROUP_ID: merchantGroupID, MENU_GROUP_ID: menuGroupID }),
      LOAD_TIME: t1 - t0,
      LOADING_STATUS: "SUCCESS",
      REASON: "",
      ERROR_MSG: "",
      NUM_ITEMS_PHOTO_MISSING: getItemWithoutPhotoCount(items),
      ...(resp?.type === "SKIP_FETCH" ? { IS_PARTIAL_MENU: true } : {}),
      ...eventLabels,
    }

    if (resp?.type?.endsWith("_FAIL")) {
      labels = {
        ...labels,
        LOADING_STATUS: "FAILURE",
        ...getResponseErrorEventLabels(resp),
      }
    }

    TroyWebTracker.sendEvent({
      category,
      action: EventAction.pageView,
      labels,
    })
    setHasInitialised(true)
  }

  useEffect(() => {
    if (
      tabKey &&
      !!shownTabs.length &&
      !shownTabs.includes(tabKey as MenuEditorTabKey)
    ) {
      // replace url of invalid tabkey
      history.replace(`${routePrefix}/${menuParentID}`)
    }
  }, [shownTabs, routePrefix, menuParentID, tabKey, history])

  useEffect(() => {
    if (shownTabs.length && !isLoadingRtkQueryMigrationEndpointsFlag) {
      fetchMenu()
    }
    // isFetchMerchantDetailRtkEnabled resolves in the same render as
    // isLoadingRtkQueryMigrationEndpointsFlag (same hook), so the correct
    // flag value is always captured when this effect fires.
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [
    shownTabs,
    isLoadingRtkQueryMigrationEndpointsFlag,
    isFetchMerchantDetailRtkEnabled,
    isFetchMenuGroupDetailRtkEnabled,
    // Router reuses this component between menu-group URLs, and shownTabs can stay
    // identical across that navigation - without the page identity here the effect
    // would not re-run, so the previous run would never be superseded and the newly
    // selected menu group would never be fetched.
    menuParentID,
    menuGroupID,
    merchantID,
  ])

  return (
    <Fragment>
      <StockManagementAutoScrollProvider>
        <MenuEditorContent
          data-testid="menu-editor-content"
          shownTabs={shownTabs}
          routePrefix={routePrefix}
          hideContent={!hasInitialised || !shownTabs.length}
        />
        <MenuDrawerContainer />
      </StockManagementAutoScrollProvider>
    </Fragment>
  )
}

export const MenuEditorWithHeaderWrapped = ({
  setHeaderTitle,
  setBackLink,
}: WithMenuHeaderInjectedProps) => {
  const { isMartStore, outletV2, isMenuGroup } = useSelector(
    (state: AppState) => state.menu,
  )
  const merchantName = outletV2?.name
  const location = useLocation()
  const history = useHistory()
  const dispatch = useDispatch()

  const handleFixPathname = () => {
    let newPathname = location.pathname
    newPathname = isMartStore
      ? location.pathname.replace(MENUS, CATALOGUES)
      : location.pathname.replace(CATALOGUES, MENUS)
    if (location.pathname !== newPathname)
      history.replace({
        pathname: newPathname,
        search: location.search,
      })
  }

  const textMap = useTranslationsMap({
    titleMenu: "menu.title",
    titleCatalogue: "catalogue.title",
  })

  const defaultTitle = isMartStore ? textMap.titleCatalogue : textMap.titleMenu

  const { isSingleStoreUser } = useSingleStoreUser()
  const updateHeader = () => {
    // Show the name of the store in detail page
    setHeaderTitle(merchantName ?? defaultTitle)

    /**
     * check if the `isSingleStoreUser` value is explicitly set to `false`
     * to ensure function only runs after the data has been loaded
     */
    if (isSingleStoreUser === false || isMenuGroup) {
      const backLink = isMartStore ? CATALOGUES : MENUS

      setBackLink(backLink)
    }
  }

  useEffect(updateHeader, [merchantName, isSingleStoreUser])

  useEffect(handleFixPathname, [
    history,
    isMartStore,
    location.pathname,
    location.search,
  ])

  // unmount
  useEffect(
    () => () => {
      dispatch(actionCreator.menu.clearStore())
    },
    [dispatch],
  )
  return <MenuEditor />
}

export const MenuEditorWithHeader = WithMenuHeader()(
  MenuEditorWithHeaderWrapped,
)
